import {
  DEFAULT_RUNTIME_CONFIG,
  RuntimeConfig,
  type RuntimeMediaDelivery,
} from "../../../utils/runtimeConfig";
import {
  RenderQuality,
  RenderQualityCaps,
  normalizeRenderQuality,
  resolveRenderQualityCaps,
} from "../renderer/qualityCaps";

type MaybePromise<T> = T | Promise<T>;

export type ArtetraRuntimeGeneration = {
  sessionId: string;
  generation: number;
  token: string;
  label?: string;
};

export type ArtetraRuntimeDisposalHook = () => MaybePromise<void>;

export type ArtetraRuntimeCleanupErrorCounts = {
  error: number;
  unknown: number;
};

export type ArtetraRuntimeCleanupResult = {
  disposedHooks: number;
  releasedAssetHandles: number;
  cleanupErrorCounts: ArtetraRuntimeCleanupErrorCounts;
};

export type ArtetraRuntimeAssetHandle = {
  id: string;
  kind: string;
  label?: string;
  release: () => boolean;
};

export type ArtetraRuntimeSessionSnapshot = {
  sessionId: string;
  initialized: boolean;
  runtimeConfigVersion: string;
  runtimeConfigSource: RuntimeConfig["source"];
  optimizedAssetsEnabled: boolean;
  mediaDelivery: RuntimeMediaDelivery | null;
  selectedQuality: RenderQuality;
  selectedCaps: RenderQualityCaps;
  progressiveLoadingEnabled: boolean;
  generation: number;
  generationToken: string;
  registeredDisposals: number;
  activeAssetHandles: number;
  activeAssetCounts: Record<string, number>;
  cleanupErrorCounts: ArtetraRuntimeCleanupErrorCounts;
};

export type CreateArtetraRuntimeSessionOptions = {
  loadRuntimeConfig?: () => MaybePromise<RuntimeConfig>;
  getQuality?: () => MaybePromise<unknown>;
};

type DisposalEntry = {
  label: string;
  hook: ArtetraRuntimeDisposalHook;
};

type AssetEntry = {
  kind: string;
  label?: string;
};

const SNAPSHOT_MAX_COUNT = 9999;
const SNAPSHOT_MAX_KEYS = 32;
const SNAPSHOT_MAX_LABEL_LENGTH = 80;

let sessionCounter = 0;

const isSafePositiveNumber = (value: unknown): value is number => (
  typeof value === "number" && Number.isFinite(value) && value > 0
);

const clampCount = (count: number) => Math.min(SNAPSHOT_MAX_COUNT, Math.max(0, Math.floor(count)));

const boundedString = (value: unknown, fallback: string, maxLength = SNAPSHOT_MAX_LABEL_LENGTH) => {
  const text = typeof value === "string" && value.trim() ? value.trim() : fallback;
  return text.length > maxLength ? text.slice(0, maxLength) : text;
};

const createSessionId = () => {
  sessionCounter += 1;
  return `artetra-runtime-${Date.now().toString(36)}-${sessionCounter.toString(36)}`;
};

const createGenerationToken = (sessionId: string, generation: number) => `${sessionId}:generation:${generation}`;

const normalizeRuntimeConfig = (config: RuntimeConfig | null | undefined): RuntimeConfig => {
  if (!config || typeof config !== "object" || !config.renderer) {
    return DEFAULT_RUNTIME_CONFIG;
  }

  return {
    ...DEFAULT_RUNTIME_CONFIG,
    ...config,
    renderer: {
      ...DEFAULT_RUNTIME_CONFIG.renderer,
      ...config.renderer,
      defaultQualityProfile: normalizeRenderQuality(config.renderer.defaultQualityProfile),
      maxPixelRatio: isSafePositiveNumber(config.renderer.maxPixelRatio)
        ? config.renderer.maxPixelRatio
        : DEFAULT_RUNTIME_CONFIG.renderer.maxPixelRatio,
      allowHighQuality: config.renderer.allowHighQuality !== false,
      qualityCaps: {
        ...DEFAULT_RUNTIME_CONFIG.renderer.qualityCaps,
        ...(config.renderer.qualityCaps || {}),
      },
    },
  };
};

const isRenderQuality = (value: unknown): value is RenderQuality => (
  value === "LQ" || value === "MQ" || value === "HQ"
);

const resolveQuality = async (
  config: RuntimeConfig,
  getQuality?: () => MaybePromise<unknown>,
): Promise<RenderQuality> => {
  let requestedQuality: unknown = config.renderer.defaultQualityProfile;

  if (getQuality) {
    try {
      const localQuality = await getQuality();
      if (isRenderQuality(localQuality)) {
        requestedQuality = localQuality;
      }
    } catch {
      requestedQuality = config.renderer.defaultQualityProfile;
    }
  }

  const quality = normalizeRenderQuality(requestedQuality);
  return quality === "HQ" && !config.renderer.allowHighQuality ? "MQ" : quality;
};

const resolveCaps = (config: RuntimeConfig, quality: RenderQuality): RenderQualityCaps => {
  const configuredCaps = config.renderer.qualityCaps?.[quality] || DEFAULT_RUNTIME_CONFIG.renderer.qualityCaps[quality];
  const maxPixelRatio = isSafePositiveNumber(config.renderer.maxPixelRatio)
    ? config.renderer.maxPixelRatio
    : DEFAULT_RUNTIME_CONFIG.renderer.maxPixelRatio;
  const pixelRatioMax = isSafePositiveNumber(configuredCaps.pixelRatio)
    ? Math.min(configuredCaps.pixelRatio, maxPixelRatio)
    : maxPixelRatio;

  return resolveRenderQualityCaps({
    quality,
    devicePixelRatio: maxPixelRatio,
    overrides: {
      pixelRatioMax,
      postProcessingScale: configuredCaps.postProcessingScale,
      shadowMapSize: configuredCaps.shadowMapSize,
    },
  });
};

const createCleanupErrorCounts = (): ArtetraRuntimeCleanupErrorCounts => ({
  error: 0,
  unknown: 0,
});

const snapshotCleanupErrorCounts = (
  counts: ArtetraRuntimeCleanupErrorCounts,
): ArtetraRuntimeCleanupErrorCounts => ({
  error: clampCount(counts.error),
  unknown: clampCount(counts.unknown),
});

const snapshotCounts = (counts: Record<string, number>) => {
  return Object.keys(counts)
    .sort()
    .slice(0, SNAPSHOT_MAX_KEYS)
    .reduce<Record<string, number>>((snapshot, kind) => {
      snapshot[kind] = clampCount(counts[kind] || 0);
      return snapshot;
    }, {});
};

export const createArtetraRuntimeSession = (
  options: CreateArtetraRuntimeSessionOptions = {},
) => {
  const sessionId = createSessionId();
  const disposals = new Map<number, DisposalEntry>();
  const activeAssets = new Map<string, AssetEntry>();
  const activeAssetCounts: Record<string, number> = {};
  const cleanupErrorCounts = createCleanupErrorCounts();

  let initialized = false;
  let runtimeConfig = DEFAULT_RUNTIME_CONFIG;
  let selectedQuality = normalizeRenderQuality(DEFAULT_RUNTIME_CONFIG.renderer.defaultQualityProfile);
  let selectedCaps = resolveCaps(runtimeConfig, selectedQuality);
  let generation = 0;
  let generationToken = createGenerationToken(sessionId, generation);
  let cleanedUp = false;
  let initializePromise: Promise<ArtetraRuntimeSessionSnapshot> | null = null;
  let disposalId = 0;
  let assetId = 0;

  const rememberCleanupError = (error: unknown) => {
    const classification = error instanceof Error ? "error" : "unknown";
    cleanupErrorCounts[classification] = clampCount(
      cleanupErrorCounts[classification] + 1,
    );
    return classification;
  };

  const getSnapshot = (): ArtetraRuntimeSessionSnapshot => ({
    sessionId,
    initialized,
    runtimeConfigVersion: runtimeConfig.version,
    runtimeConfigSource: runtimeConfig.source,
    optimizedAssetsEnabled: Boolean(runtimeConfig.optimizedAssets?.enabled),
    mediaDelivery: runtimeConfig.mediaDelivery ? {
      ...runtimeConfig.mediaDelivery,
      families: [...runtimeConfig.mediaDelivery.families],
    } : null,
    selectedQuality,
    selectedCaps: { ...selectedCaps },
    progressiveLoadingEnabled: Boolean(runtimeConfig.progressiveLoading?.enabled),
    generation,
    generationToken,
    registeredDisposals: clampCount(disposals.size),
    activeAssetHandles: clampCount(activeAssets.size),
    activeAssetCounts: snapshotCounts(activeAssetCounts),
    cleanupErrorCounts: snapshotCleanupErrorCounts(cleanupErrorCounts),
  });

  const initialize = async () => {
    if (cleanedUp) return getSnapshot();

    if (!initializePromise) {
      initializePromise = Promise.resolve()
        .then(() => options.loadRuntimeConfig ? options.loadRuntimeConfig() : DEFAULT_RUNTIME_CONFIG)
        .then((config) => normalizeRuntimeConfig(config))
        .catch(() => DEFAULT_RUNTIME_CONFIG)
        .then(async (config) => {
          const nextQuality = await resolveQuality(config, options.getQuality);
          if (cleanedUp) return getSnapshot();

          runtimeConfig = config;
          selectedQuality = nextQuality;
          selectedCaps = resolveCaps(config, nextQuality);
          initialized = true;
          return getSnapshot();
        });
    }

    return initializePromise;
  };

  const beginGeneration = (label?: string): ArtetraRuntimeGeneration => {
    if (cleanedUp) {
      return {
        sessionId,
        generation,
        token: generationToken,
        ...(label ? { label: boundedString(label, label) } : {}),
      };
    }

    generation += 1;
    generationToken = createGenerationToken(sessionId, generation);

    return {
      sessionId,
      generation,
      token: generationToken,
      ...(label ? { label: boundedString(label, label) } : {}),
    };
  };

  const isCurrentGeneration = (candidate: ArtetraRuntimeGeneration | string | null | undefined) => {
    const token = typeof candidate === "string" ? candidate : candidate?.token;
    return !cleanedUp && token === generationToken;
  };

  const registerDisposal = (hook: ArtetraRuntimeDisposalHook, label?: string) => {
    if (cleanedUp) return () => false;

    disposalId += 1;
    const id = disposalId;
    disposals.set(id, {
      hook,
      label: boundedString(label, `disposal-${id}`),
    });

    return () => disposals.delete(id);
  };

  const releaseAssetHandle = (handle: ArtetraRuntimeAssetHandle | string | null | undefined) => {
    if (cleanedUp) return false;

    const id = typeof handle === "string" ? handle : handle?.id;
    if (!id || !activeAssets.has(id)) return false;

    const asset = activeAssets.get(id);
    activeAssets.delete(id);
    if (asset) {
      activeAssetCounts[asset.kind] = Math.max(0, (activeAssetCounts[asset.kind] || 0) - 1);
      if (activeAssetCounts[asset.kind] === 0) {
        delete activeAssetCounts[asset.kind];
      }
    }

    return true;
  };

  const createAssetHandle = (kind: string, label?: string): ArtetraRuntimeAssetHandle => {
    const safeKind = boundedString(kind, "other", 40);
    if (cleanedUp) {
      return {
        id: `${sessionId}:asset:closed`,
        kind: safeKind,
        ...(label ? { label: boundedString(label, label) } : {}),
        release: () => false,
      };
    }

    assetId += 1;
    const id = `${sessionId}:asset:${assetId}`;
    activeAssets.set(id, { kind: safeKind, label });
    activeAssetCounts[safeKind] = clampCount((activeAssetCounts[safeKind] || 0) + 1);

    return {
      id,
      kind: safeKind,
      ...(label ? { label: boundedString(label, label) } : {}),
      release: () => releaseAssetHandle(id),
    };
  };

  const cleanup = async (): Promise<ArtetraRuntimeCleanupResult> => {
    if (cleanedUp) {
      return {
        disposedHooks: 0,
        releasedAssetHandles: 0,
        cleanupErrorCounts: createCleanupErrorCounts(),
      };
    }

    cleanedUp = true;
    generation += 1;
    generationToken = createGenerationToken(sessionId, generation);
    const pendingDisposals = Array.from(disposals.values());
    disposals.clear();
    const currentCleanupErrorCounts = createCleanupErrorCounts();

    for (const disposal of pendingDisposals) {
      try {
        await disposal.hook();
      } catch (error) {
        const classification = rememberCleanupError(error);
        currentCleanupErrorCounts[classification] = clampCount(
          currentCleanupErrorCounts[classification] + 1,
        );
      }
    }

    const releasedAssetHandles = activeAssets.size;
    activeAssets.clear();
    Object.keys(activeAssetCounts).forEach((kind) => {
      delete activeAssetCounts[kind];
    });

    return {
      disposedHooks: pendingDisposals.length,
      releasedAssetHandles,
      cleanupErrorCounts: currentCleanupErrorCounts,
    };
  };

  return {
    initialize,
    beginGeneration,
    isCurrentGeneration,
    registerDisposal,
    createAssetHandle,
    releaseAssetHandle,
    getSnapshot,
    cleanup,
  };
};
